import { useQuery } from '@apollo/client';
import { Box } from '@mui/material';
import { Metadata, MetadataItem, MetadataItemExpandable } from 'components/common/metadata';
import { useQueryParamState } from 'hooks/use-query-params';
import { useAuthContext } from 'providers/auth';
import { useDemoMode } from 'providers/demo-mode';
import { usePageContext } from 'providers/page';
import { FC, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import {
  Conversation,
  Conversation_externalLead,
  Conversation_externalLead_conversation,
  ConversationVariables,
} from 'types/apollo/Conversation';
import { FILL_PARENT_SX, pageHeight } from 'utils/layout';
import { ChatConversation } from './ChatConversation';
import { getInitials } from './conversation-helpers';
import { ConversationQuery } from './conversation.graphql';
import { ConversationList, PhoneInfoMetadata } from './ConversationList';
import { EditLeadStatusDialog } from './edit-lead-status-dialog';
import { transformAndEncodeConversationToExportUrl } from './export-conversation';
import { sortedMetadataEntries, UNCOPIABLE_VALUES } from './metadata-entries';
import { addPlusOne } from './phone-number';

// Re-export ChatConversation for backwards compatibility
export { ChatConversation } from './ChatConversation';

export const ConversationPage: FC<{ hideFilters?: boolean }> = ({ hideFilters }) => {
  const { id: companyId, campaignId } = useParams<{ id: string; campaignId: string }>();
  const { isInternal } = useAuthContext();
  const { fullHeight } = usePageContext();
  const [lead, setLead] = useState<Conversation_externalLead | null>(null);
  const [phone] = useQueryParamState('phone');
  const [searchParams, setSearchParams] = useSearchParams();
  const campaignIdParam = searchParams.get('campaignId');
  const [conversation, setConversation] = useState<Conversation_externalLead_conversation[] | null>(
    null,
  );
  const [loadingConversation, setLoadingConversation] = useState(false);
  const { getRandomFirstName, isInDemoMode } = useDemoMode();
  const metadataEntries = useMemo(() => sortedMetadataEntries(lead?.metadata), [lead?.metadata]);

  const { refetch } = useQuery<Conversation, ConversationVariables>(ConversationQuery, {
    skip: true,
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      setLead(data.externalLead);
      setConversation(data.externalLead.conversation);
    },
  });

  const fetchConversation = async (variables?: ConversationVariables) => {
    try {
      setLoadingConversation(true);
      await refetch({
        sinceIso: new Date().toISOString(),
        ...variables,
      });
    } finally {
      setLoadingConversation(false);
    }
  };

  const handleExport = async () => {
    const conversationData = await transformAndEncodeConversationToExportUrl(conversation ?? []);
    if (!campaignId) {
      const newExportUrl = `campaigns/${campaignIdParam}/${conversationData}`;
      window.open(newExportUrl, '_blank');
    } else {
      window.open(conversationData, '_blank');
    }
  };

  useEffect(() => {
    if (phone && phone.length >= 10) {
      const phoneWithPlusOne = addPlusOne(phone);
      if (phoneWithPlusOne) {
        fetchConversation({
          ...(campaignId && { campaignId }),
          ...(companyId && { companyId }),
          ...(!campaignId && campaignIdParam && { campaignId: campaignIdParam }),
          phone: phoneWithPlusOne,
          includeCampaignName: !campaignId,
        });
      }
    }
  }, [campaignIdParam, phone]);

  const updateSelection = (phoneNumber: string, campaignIdForUrl?: string) => {
    const newParams = new URLSearchParams(searchParams);

    if (phoneNumber) {
      newParams.set('phone', phoneNumber);
    } else {
      newParams.delete('phone');
    }

    if (!campaignId) {
      if (campaignIdForUrl) {
        newParams.set('campaignId', campaignIdForUrl);
      } else {
        newParams.delete('campaignId');
      }
    } else {
      newParams.delete('campaignId');
    }

    setSearchParams(newParams);
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          // Below md the shell isn't full-height, so the row sizes itself.
          ...(fullHeight ? FILL_PARENT_SX : { height: pageHeight(), flexGrow: 1 }),
          maxWidth: '100%',
          overflow: 'hidden',
        }}
      >
        <ConversationList
          fetchConversation={fetchConversation}
          loadingConversation={loadingConversation}
          onSelect={(phone, campaignIdForUrl) => updateSelection(phone, campaignIdForUrl)}
          selectedPhone={phone}
          hideFilters={hideFilters}
          companyId={companyId}
        >
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <ChatConversation
              messages={conversation}
              loading={loadingConversation}
              leadInitials={getInitials(lead?.firstName, lead?.lastName)}
              leadTimeZone={lead?.timezone ?? 'America/Denver'}
              onExport={handleExport}
              campaignId={campaignId || campaignIdParam!}
              phone={phone}
              fetchConversation={fetchConversation}
              scheduledOutreach={lead?.scheduledOutreach ?? []}
              repostedAt={lead?.reposted ? new Date(lead.reposted) : null}
            />
          </Box>
        </ConversationList>
      </Box>
      <Metadata>
        {lead && (
          <>
            <MetadataItem
              label="First name"
              value={getRandomFirstName(lead.firstName ?? '(not set)', lead.phone)}
            />
            {!isInDemoMode && (
              <MetadataItem label="Last name" value={lead.lastName ?? '(not set)'} />
            )}
            {!isInDemoMode && <MetadataItem label="Phone" value={lead.phone} />}
            <MetadataItem label="Region" value={lead.region ?? '(not set)'} />
            {isInternal && lead.status ? (
              <EditLeadStatusDialog
                campaignId={campaignId || campaignIdParam!}
                phone={lead.phone}
                currentStatus={lead.status}
                currentUndeliverableReason={lead.undeliverableReason}
                onSuccess={() => {
                  fetchConversation({
                    ...(campaignId && { campaignId }),
                    ...(companyId && { companyId }),
                    ...(!campaignId && campaignIdParam && { campaignId: campaignIdParam }),
                    phone: lead.phone,
                    includeCampaignName: !campaignId,
                  });
                }}
              />
            ) : (
              <MetadataItem label="Status" value={lead.status ?? '(not set)'} />
            )}

            {lead.undeliverableReason && (
              <MetadataItem label="Undeliverable reason" value={lead.undeliverableReason} />
            )}
            <MetadataItem label="Timezone" value={lead.timezone} />
            {lead.phoneInfo && <PhoneInfoMetadata phoneInfo={lead.phoneInfo} />}
            {metadataEntries.length > 0 && (
              <MetadataItemExpandable
                text="Metadata"
                values={metadataEntries}
                itemRenderer={(key, value) =>
                  !UNCOPIABLE_VALUES.has(value as string) ? (
                    <MetadataItem.Copy label={key} value={value} />
                  ) : (
                    <MetadataItem label={key} value={value} />
                  )
                }
              />
            )}
          </>
        )}
      </Metadata>
    </>
  );
};

export default ConversationPage;
